import React, { useEffect, useState, useMemo } from "react";
import {
  Box, Typography, TextField, Button, Grid,
  FormControl, InputLabel, Select, MenuItem, OutlinedInput,
} from "@mui/material";
import { useParams, useNavigate } from "react-router-dom";
import { useIntl } from "react-intl";

import {
  fetchStrategicIssues, updateStrategicIssue,
  fetchValueChains,
  fetchBusinessProcesses,
  fetchFinancialGoals,
  fetchStrategicGoals,
  fetchCustomerValues,
} from "../../api/companyApi";
import { useNotification } from "../../providers/NotificationProvider";
import { useAuth } from "../../providers/AuthProvider";

export default function SmStrategicIssueEditPage() {
  const { businessDomainId, successFactorId } = useParams();
  const navigate = useNavigate();
  const intl = useIntl();
  const notify = useNotification();
  const { companyId } = useAuth();
  const t = (id) => intl.formatMessage({ id });

  const [successFactorName, setSuccessFactorName] = useState("");
  const [successFactorDescription, setSuccessFactorDescription] = useState("");
  const [selectedBPIds, setSelectedBPIds] = useState([]);
  const [selectedNames, setSelectedNames] = useState([]);
  const [sourceIds, setSourceIds] = useState([]);
  const [valueChains, setValueChains] = useState([]);
  const [childBusinessProcesses, setChildBusinessProcesses] = useState([]);
  const [businessProcesses, setBusinessProcesses] = useState([]);
  const [financialGoals, setFinancialGoals] = useState([]);
  const [strategicGoals, setStrategicGoals] = useState([]);
  const [customerValues, setCustomerValues] = useState([]);
  const [strategicIssues, setStrategicIssues] = useState([]);
  const [fullData, setFullData] = useState(null);

  const selectedBPs = selectedBPIds.map((id) => businessProcesses.find((bp) => bp.businessProcessId === id)).filter(Boolean);

  // 親バリューチェーンマップ
  const parentVcMap = useMemo(() => {
    const map = {};
    valueChains.forEach((vc) => {
      (vc.associatedBusinessProcesses || []).forEach((bp) => {
        map[bp.businessProcessId] = vc.businessProcessName;
      });
    });
    return map;
  }, [valueChains]);

  // 指標は選択されたBP（バリューチェーン＋子BP）の indicators を集約
  const sourceIndicators = useMemo(() => {
    const all = selectedBPs.flatMap((bp) => {
      const parentVcName = parentVcMap[bp.businessProcessId];
      return (bp.indicators || []).map((ind) => ({
        ...ind,
        _bpName: bp.businessProcessName,
        _parentVcName: parentVcName || null,
      }));
    });
    const seen = new Set();
    return all.filter((ind) => {
      if (seen.has(ind.indicatorName)) return false;
      seen.add(ind.indicatorName);
      return true;
    });
  }, [selectedBPs, parentVcMap]);

  useEffect(() => {
    (async () => {
      try {
        const [issues, bps, childBps, fgoals, sgoals, cvals] = await Promise.all([
          fetchStrategicIssues(businessDomainId),
          fetchValueChains(businessDomainId).catch(() => []),
          fetchBusinessProcesses(businessDomainId).catch(() => []),
          fetchFinancialGoals(businessDomainId).catch(() => []),
          fetchStrategicGoals(businessDomainId).catch(() => []),
          fetchCustomerValues(businessDomainId).catch(() => []),
        ]);
        const vcArray = Array.isArray(bps) ? bps : [];
        const childArray = Array.isArray(childBps) ? childBps : [];
        setValueChains(vcArray);
        setChildBusinessProcesses(childArray);
        setBusinessProcesses([...vcArray, ...childArray]);
        setFinancialGoals(Array.isArray(fgoals) ? fgoals : []);
        setStrategicGoals(Array.isArray(sgoals) ? sgoals : []);
        setCustomerValues(Array.isArray(cvals) ? cvals : []);
        const issuesArray = Array.isArray(issues) ? issues : [];
        setStrategicIssues(issuesArray.filter((i) => String(i.successFactorId) !== String(successFactorId)));
        const found = issuesArray.find(
          (g) => String(g.successFactorId) === String(successFactorId)
        );
        if (found) {
          setFullData(found);
          setSuccessFactorName(found.successFactorName || "");
          setSuccessFactorDescription(found.successFactorDescription || "");
          setSourceIds((found.sources || []).map((s) => s.successFactorId));
          setSelectedNames((found.indicators || []).map((ind) => ind.indicatorName));
          // バックエンドのバグ（addAll のみで clear なし）により DB に重複が蓄積している場合があるため重複排除
          const uniqueBPIds = [...new Set((found.businessProcesses || []).map((bp) => bp.businessProcessId))];
          setSelectedBPIds(uniqueBPIds);
        }
      } catch (e) {
        notify.apiError(e);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessDomainId, successFactorId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (sourceIndicators.length > 0 && selectedNames.length === 0) {
      notify.error(t("financialGoal.indicatorRequired"));
      return;
    }
    try {
      const indicators = selectedNames
        .map((name) => sourceIndicators.find((ind) => ind.indicatorName === name))
        .filter(Boolean);
      const sources = sourceIds.map((id) => allSources.find((s) => s.successFactorId === id)).filter(Boolean);
      await updateStrategicIssue({
        ...fullData,
        successFactorName,
        successFactorDescription,
        businessProcesses: selectedBPs,
        indicators,
        sources,
        businessDomainId,
        companyId,
        successFactorId,
      });
      notify.success(t("message.update.success"));
      navigate(`/design/${businessDomainId}/strategy-map/strategic-issues/${successFactorId}`);
    } catch (err) {
      notify.apiError(err);
    }
  };

  if (!fullData) return null;

  const allSources = [...financialGoals, ...customerValues, ...strategicGoals, ...strategicIssues];

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 2 }}>
        {t("strategicIssue.edit.title")}
      </Typography>

      <Box component="form" onSubmit={handleSubmit}>
        <Grid container spacing={2}>
          <Grid size={12}>
            <TextField
              fullWidth required
              label={t("successFactor.name")}
              value={successFactorName}
              onChange={(e) => setSuccessFactorName(e.target.value)}
            />
          </Grid>

          <Grid size={12}>
            <TextField
              fullWidth multiline rows={3}
              label={t("successFactor.description")}
              value={successFactorDescription}
              onChange={(e) => setSuccessFactorDescription(e.target.value)}
            />
          </Grid>

          <Grid size={12}>
            <FormControl fullWidth>
              <InputLabel>{t("strategicIssue.businessProcess.label")}</InputLabel>
              <Select
                multiple
                value={selectedBPIds}
                onChange={(e) => setSelectedBPIds(e.target.value)}
                input={<OutlinedInput label={t("strategicIssue.businessProcess.label")} />}
              >
                {valueChains.length > 0 && [
                  <MenuItem key="__vc_hd" disabled sx={{ fontWeight: 700, opacity: 1, color: "text.primary" }}>── {t("valueChain.list.title")} ──</MenuItem>,
                  ...valueChains.map((bp) => (
                    <MenuItem key={bp.businessProcessId} value={bp.businessProcessId}>
                      {bp.businessProcessName}
                    </MenuItem>
                  )),
                ]}
                {childBusinessProcesses.length > 0 && [
                  <MenuItem key="__bp_hd" disabled sx={{ fontWeight: 700, opacity: 1, color: "text.primary" }}>── {t("businessProcess.list.title")} ──</MenuItem>,
                  ...childBusinessProcesses.map((bp) => {
                    const vcName = parentVcMap[bp.businessProcessId];
                    return (
                      <MenuItem key={bp.businessProcessId} value={bp.businessProcessId}>
                        {vcName ? `${vcName} > ${bp.businessProcessName}` : bp.businessProcessName}
                      </MenuItem>
                    );
                  }),
                ]}
              </Select>
            </FormControl>
          </Grid>

          <Grid size={12}>
            <FormControl fullWidth>
              <InputLabel>{t("strategicIssue.sources.label")}</InputLabel>
              <Select
                multiple
                value={sourceIds}
                onChange={(e) => setSourceIds(e.target.value)}
                input={<OutlinedInput label={t("strategicIssue.sources.label")} />}
              >
                {financialGoals.length > 0 && [
                  <MenuItem key="__fg_hd" disabled sx={{ fontWeight: 700, opacity: 1, color: "text.primary" }}>── {t("financialGoal.list.title")} ──</MenuItem>,
                  ...financialGoals.map((item) => (
                    <MenuItem key={item.successFactorId} value={item.successFactorId}>{item.successFactorName}</MenuItem>
                  )),
                ]}
                {customerValues.length > 0 && [
                  <MenuItem key="__cv_hd" disabled sx={{ fontWeight: 700, opacity: 1, color: "text.primary" }}>── {t("customerValue.list.title")} ──</MenuItem>,
                  ...customerValues.map((item) => (
                    <MenuItem key={item.successFactorId} value={item.successFactorId}>{item.successFactorName}</MenuItem>
                  )),
                ]}
                {strategicGoals.length > 0 && [
                  <MenuItem key="__sg_hd" disabled sx={{ fontWeight: 700, opacity: 1, color: "text.primary" }}>── {t("strategicGoal.list.title")} ──</MenuItem>,
                  ...strategicGoals.map((item) => (
                    <MenuItem key={item.successFactorId} value={item.successFactorId}>{item.successFactorName}</MenuItem>
                  )),
                ]}
                {strategicIssues.length > 0 && [
                  <MenuItem key="__si_hd" disabled sx={{ fontWeight: 700, opacity: 1, color: "text.primary" }}>── {t("strategicIssue.list.title")} ──</MenuItem>,
                  ...strategicIssues.map((item) => (
                    <MenuItem key={item.successFactorId} value={item.successFactorId}>{item.successFactorName}</MenuItem>
                  )),
                ]}
              </Select>
            </FormControl>
          </Grid>

          <Grid size={12}>
            <FormControl fullWidth>
              <InputLabel>{t("financialGoal.selectIndicatorHint")}</InputLabel>
              <Select
                multiple
                disabled={sourceIndicators.length === 0}
                value={selectedNames}
                onChange={(e) => setSelectedNames(e.target.value)}
                input={<OutlinedInput label={t("financialGoal.selectIndicatorHint")} />}
              >
                {sourceIndicators.map((ind) => {
                  const prefix = ind._parentVcName
                    ? `[${ind._parentVcName} > ${ind._bpName}] `
                    : `[${ind._bpName}] `;
                  return (
                    <MenuItem key={ind.indicatorName} value={ind.indicatorName}>
                      {prefix}{ind.indicatorName}（{ind.unit}）
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
          </Grid>
        </Grid>

        <Box sx={{ mt: 3, display: "flex", gap: 2 }}>
          <Button variant="contained" type="submit" disabled={!successFactorName.trim()}>
            {t("common.save")}
          </Button>
          <Button
            variant="outlined"
            onClick={() => navigate(`/design/${businessDomainId}/strategy-map/strategic-issues/${successFactorId}`)}
          >
            {t("common.cancel")}
          </Button>
        </Box>
      </Box>
    </Box>
  );
}
